import Exercise from '../storage/Exercise.js'
import ExerciseStore from '../storage/ExerciseStore.js'

class ExerciseDialog extends HTMLElement {

  static observedAttributes = ["mode", "type", "open"]

  #dialog = null
  #mode = null

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "mode") {
      this.#mode = newValue
    }
    else if (name === "type") {
      // drives CSS visibility of laterality/measure controls and cardio unit
    }
    else if (name === "open") {
      // drives CSS show/hide and animation
    }
    else {
      console.warn("Unknown attribute %s given value %s", name, newValue)
    }
    this.#render()
  }

  #render() {
  }

  show() {
    this.setAttribute("mode", "create")
    const form = this.#dialog.querySelector("form")
    form.reset()
    form.elements["exerciseId"].value = ""
    form.elements["exerciseName"].setCustomValidity("")
    this.setAttribute("type", form.elements["exerciseType"].value)
    this.querySelector("[role='alert']").setAttribute("hidden", "")
    const values = this.querySelector("gime-values")
    values.setAttribute("laterality", form.elements["laterality"].value)
    values.setAttribute("measure", form.elements["measure"].value)
    this.setAttribute("open", "")
    this.removeAttribute("aria-hidden")
    setTimeout( () => {
      this.querySelector("input[name='exerciseName']").focus()
    }, 255)
  }

  showEdit(exercise) {
    this.setAttribute("mode", "edit")
    const form = this.#dialog.querySelector("form")
    form.reset()

    form.elements["exerciseId"].value = exercise.id
    form.elements["exerciseName"].value = exercise.exerciseName
    form.elements["exerciseType"].value = exercise.exerciseType
    form.elements["laterality"].value = exercise.laterality
    form.elements["measure"].value = exercise.measure
    form.elements["valueLeft"].value = exercise.valueLeft
    form.elements["valueRight"].value = exercise.valueRight ?? ""
    form.elements["sets"].value = exercise.sets ?? 1

    const values = this.querySelector("gime-values")
    values.setAttribute("laterality", exercise.laterality)
    values.setAttribute("measure", exercise.measure)

    this.setAttribute("type", exercise.exerciseType)

    form.elements["exerciseName"].setCustomValidity("")
    this.querySelector("[role='alert']").setAttribute("hidden", "")
    this.setAttribute("open", "")
    this.removeAttribute("aria-hidden")
    this.querySelector("input[name='exerciseName']").focus()
  }

  #closeDialog = () => {
    this.removeAttribute("open")
    this.setAttribute("aria-hidden", "true")
  }

  #close = (event) => {
    event.preventDefault()
    this.#closeDialog()
  }

  #keydown = (event) => {
    if (event.key === "Escape") {
      this.#closeDialog()
    }
  }

  #nameInputChanged = () => {
    const nameInput = this.#dialog.querySelector("[name='exerciseName']")
    nameInput.setCustomValidity("")
    this.querySelector("[role='alert']").setAttribute("hidden", "")
  }

  #typeChanged = (event) => {
    const exerciseType = event.currentTarget.value
    this.setAttribute("type", exerciseType)
    const form = this.#dialog.querySelector("form")
    const values = this.querySelector("gime-values")
    const noValues = !form.elements["valueLeft"].value && !form.elements["valueRight"].value
    if (exerciseType === "cardio") {
      form.elements["laterality"].value = "bilateral"
      form.elements["measure"].value = "duration"
      if (noValues) {
        form.elements["valueLeft"].value = "30"
      }
      values.setAttribute("laterality", "bilateral")
      values.setAttribute("measure", "duration")
    }
    else if (exerciseType === "stretch") {
      if (noValues) {
        form.elements["laterality"].value = "bilateral"
        form.elements["measure"].value = "duration"
        form.elements["valueLeft"].value = "30"
        values.setAttribute("laterality", "bilateral")
        values.setAttribute("measure", "duration")
      }
      else {
        values.setAttribute("laterality", form.elements["laterality"].value)
        values.setAttribute("measure", form.elements["measure"].value)
      }
    }
    else {
      values.setAttribute("laterality", form.elements["laterality"].value)
      values.setAttribute("measure", form.elements["measure"].value)
    }
  }

  #measureChanged = (event) => {
    const value = event.currentTarget.value
    const values = this.querySelector("gime-values")
    if (values) {
      if (value === "weight") {
        values.setAttribute("measure","weight")
      }
      else if (value === "duration") {
        values.setAttribute("measure","duration")
      }
      else {
        values.removeAttribute("measure")
      }
    }
    else {
      console.warn("No gime-values inside this gime-exercise-dialog - nothing will work properly")
    }
  }

  #lateralityChanged = (event) => {
    const value = event.currentTarget.value
    const values = this.querySelector("gime-values")
    if (values) {
      if (value === "unilateral") {
        values.setAttribute("laterality", "unilateral")
      }
      else if (value === "bilateral") {
        values.setAttribute("laterality", "bilateral")
      }
      else {
        values.removeAttribute("laterality")
      }
    }
    else {
      console.warn("No gime-values inside this gime-exercise-dialog - nothing will work properly")
    }
  }

  #submit = (event) => {
    event.preventDefault()
    const form = event.currentTarget
    const formData = Object.fromEntries(new FormData(form))
    const nameInput = form.elements["exerciseName"]
    const { exerciseId, sets, ...fields } = formData

    if (fields.exerciseType !== "cardio") {
      fields.sets = Number(sets)
    }

    let exercise
    if (exerciseId) {
      exercise = ExerciseStore.get(exerciseId)
      Object.assign(exercise, fields)
    }
    else {
      exercise = new Exercise(fields)
    }

    if (ExerciseStore.isNameTaken(exercise)) {
      const duplicateAlert = this.querySelector("[role='alert'][data-duplicate]")
      nameInput.setCustomValidity(duplicateAlert.textContent)
      nameInput.reportValidity()
      duplicateAlert.removeAttribute("hidden")
      return
    }

    nameInput.setCustomValidity("")

    let savedExercise
    if (exerciseId) {
      savedExercise = ExerciseStore.update(exercise)
    }
    else {
      savedExercise = ExerciseStore.create(exercise)
    }

    this.dispatchEvent(new CustomEvent("gime-save", { detail: savedExercise, bubbles: true }))
    this.#closeDialog()
  }

  connectedCallback() {
    if (! this.#dialog ) {
      this.#dialog = this.querySelector("[role='dialog']")
    }
    this.querySelectorAll("button[data-action='close']").forEach( (button) => {
      button.addEventListener("click", this.#close)
    })
    this.querySelectorAll("input[name=exerciseType]").forEach( (input) => {
      input.addEventListener("change", this.#typeChanged)
    })
    this.querySelectorAll("input[name=measure]").forEach( (input) => {
      input.addEventListener("change", this.#measureChanged)
    })
    this.querySelectorAll("input[name=laterality]").forEach( (input) => {
      input.addEventListener("change", this.#lateralityChanged)
    })
    this.querySelector("form").addEventListener("submit", this.#submit)
    this.querySelector("[name='exerciseName']").addEventListener("input", this.#nameInputChanged)
    document.addEventListener("keydown", this.#keydown)
  }
}

export default ExerciseDialog
